import { memo, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { UpdatesTableProps } from '@modules/updates/types'
import { Box } from '@mui/material'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { VirtualItem, useWindowVirtualizer } from '@tanstack/react-virtual'

import { StyledTableCell, TableWrapper } from '../../index.styles'
import { Update } from '../../types'
import { UpdatesTableColumnHeaders } from './UpdatesTableColumnHeaders'
import { UpdateTableRow, getTableColumnsComponents } from './UpdatesTableRow'
import { tableColumnsConfig } from './constants'

const UpdatesTable = (props: UpdatesTableProps) => {
  const {
    isPageDataLoading = false,
    updatesPageData,
    isFetchingNextPage,
    onMarkAsRead,
    onClickProfile,
    fetchNextPage,
    hasNextPage,
  } = props
  const parentRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const tableData = useMemo(
    () =>
      updatesPageData.map(row => {
        const tableColumns = getTableColumnsComponents({
          update: row,
          onMarkAsRead,
          onClickProfile,
          t,
        })

        return {
          ...row,
          columns: tableColumnsConfig.map(({ fieldName, restCellProps }) => {
            const cellComponent = tableColumns[fieldName]
            return (
              <StyledTableCell
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...restCellProps}
              >
                {cellComponent ? cellComponent() : (row[fieldName as keyof Update] as string)}
              </StyledTableCell>
            )
          }),
        }
      }),
    [updatesPageData],
  )

  const virtualizer = useWindowVirtualizer({
    count: tableData.length + 1,
    estimateSize: () => 52,
    overscan: 10,
    scrollMargin: parentRef.current?.offsetTop,
  })

  useEffect(() => {
    const [lastItem] = [...virtualizer.getVirtualItems()].reverse()
    if (!lastItem) {
      return
    }

    if (lastItem.index >= updatesPageData.length - 1 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage?.()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, updatesPageData.length, virtualizer.getVirtualItems()])

  const virtualItems = virtualizer.getVirtualItems()

  const tableElementRef = useRef<HTMLTableElement>(null)

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ width: '100%', overflowX: 'auto', marginBottom: '-16px' }}>
        <TableWrapper>
          <TableContainer
            component={Paper}
            sx={{ position: 'relative', boxShadow: 'none', overflowX: 'inherit', paddingBottom: '15px' }}
          >
            <Box ref={tableElementRef}>
              {isPageDataLoading ? (
                <TableBody>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                  <TableRow>
                    <StyledTableCell>
                      <Skeleton variant="rectangular" sx={{ my: 1, mx: 1 }} />
                    </StyledTableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <>
                  <UpdatesTableColumnHeaders tableColumns={tableColumnsConfig} />
                  <div ref={parentRef} style={{ overflowY: 'auto' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        height: virtualizer.getTotalSize(),
                        width: '100%',
                      }}
                    >
                      {virtualItems.map((virtualItem: VirtualItem) => (
                        <UpdateTableRow
                          key={virtualItem.index}
                          virtualItem={virtualItem}
                          virtualizer={virtualizer}
                          update={tableData[virtualItem.index]}
                          onMarkAsRead={onMarkAsRead}
                          onClickProfile={onClickProfile}
                        />
                      ))}
                    </Box>
                  </div>
                </>
              )}
            </Box>
          </TableContainer>
        </TableWrapper>
      </Box>
    </Box>
  )
}

export default memo(UpdatesTable)
